import { useEffect, useState } from "react";

export type AnimatedListPhase = "idle" | "enter" | "exit";

export interface AnimatedListEntry<T> {
  key: string | number;
  item: T;
  phase: AnimatedListPhase;
}

const toIdleEntries = <T,>(items: T[], getKey: (item: T) => string | number): AnimatedListEntry<T>[] =>
  items.map((item) => ({ key: getKey(item), item, phase: "idle" }));

/** 호출부가 매 렌더 새 빈 배열(`data ?? []`)을 넘겨도 같은 목록으로 보도록 항목 단위로 비교한다. */
const isSameList = <T,>(a: T[], b: T[]) => a.length === b.length && a.every((item, index) => item === b[index]);

const mergeEntries = <T,>(
  previous: AnimatedListEntry<T>[],
  items: T[],
  getKey: (item: T) => string | number
): AnimatedListEntry<T>[] => {
  const nextKeys = new Set(items.map(getKey));
  const previousByKey = new Map(previous.map((entry) => [entry.key, entry]));
  const next: AnimatedListEntry<T>[] = items.map((item) => {
    const key = getKey(item);
    const before = previousByKey.get(key);
    if (!before) return { key, item, phase: "enter" };
    return { key, item, phase: before.phase === "enter" ? "enter" : "idle" };
  });
  previous.forEach((entry, index) => {
    if (nextKeys.has(entry.key)) return;
    next.splice(Math.min(index, next.length), 0, { ...entry, phase: "exit" });
  });
  return next;
};

/**
 * 목록에 새로 들어온 항목은 `enter`, 빠진 항목은 `exitMs` 동안 `exit` 로 남겨 펼침·접힘
 * 전환을 걸 수 있게 한다. `resetKey`(예: 선택 프로젝트)가 바뀌면 전환 없이 새 목록으로 교체한다 —
 * 다른 프로젝트로 넘어갈 때 모든 행이 한꺼번에 접히고 펼쳐지는 일을 막는다.
 */
export function useAnimatedList<T>(
  items: T[],
  getKey: (item: T) => string | number,
  exitMs: number,
  resetKey: string
): AnimatedListEntry<T>[] {
  const [entries, setEntries] = useState(() => toIdleEntries(items, getKey));
  const [seenItems, setSeenItems] = useState(items);
  const [seenResetKey, setSeenResetKey] = useState(resetKey);

  // 렌더 중 이전 입력과 비교해 조정한다(React 의 "props 변화에 따른 state 조정" 패턴) —
  // effect 로 맞추면 한 프레임 동안 옛 목록이 그려진다.
  if (resetKey !== seenResetKey) {
    setSeenResetKey(resetKey);
    setSeenItems(items);
    setEntries(toIdleEntries(items, getKey));
  } else if (!isSameList(items, seenItems)) {
    setSeenItems(items);
    setEntries(mergeEntries(entries, items, getKey));
  }

  const hasExiting = entries.some((entry) => entry.phase === "exit");
  useEffect(() => {
    if (!hasExiting) return;
    const timer = setTimeout(() => {
      setEntries((previous) => previous.filter((entry) => entry.phase !== "exit"));
    }, exitMs);
    return () => clearTimeout(timer);
  }, [hasExiting, exitMs]);

  return entries;
}
